import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Group,
  Loader,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import {
  fetchPriorityCompanySuggestions,
  resolveV2Company,
} from '../../services/api';
import { SelectedChip, SuggestionChip } from '../components/WizardChips';

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function industryItems(answers) {
  if (Array.isArray(answers?.industries?.items)) return answers.industries.items;
  if (Array.isArray(answers?.industries)) return answers.industries;
  return [];
}

function locationItems(answers) {
  if (Array.isArray(answers?.where?.items)) return answers.where.items;
  if (answers?.where?.text) return [answers.where.text];
  return [];
}

export function shouldShowPriorityCompanies(answers) {
  return industryItems(answers).length >= 1;
}

export default function PriorityCompaniesSection({
  api,
  answers = {},
  value = [],
  onChange,
}) {
  const prioritised = Array.isArray(value) ? value : [];
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [draft, setDraft] = useState('');
  const [resolving, setResolving] = useState(false);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  const industries = useMemo(() => industryItems(answers), [answers]);
  const locations = useMemo(() => locationItems(answers), [answers]);

  const prioritisedKeys = useMemo(
    () => new Set(prioritised.map((entry) => normalize(entry.name))),
    [prioritised],
  );

  const visibleSuggestions = useMemo(
    () => suggestions.filter((name) => !prioritisedKeys.has(normalize(name))),
    [suggestions, prioritisedKeys],
  );

  const loadSuggestions = useCallback(async () => {
    if (!api || !industries.length || !locations.length) {
      setSuggestions([]);
      return;
    }
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoadingSuggestions(true);
    try {
      const { suggestions: rows } = await fetchPriorityCompanySuggestions(api, {
        industries,
        locations,
      });
      if (requestIdRef.current !== requestId) return;
      const names = (rows || []).map((row) => String(row?.name || '').trim()).filter(Boolean);
      setSuggestions(names);
    } catch {
      if (requestIdRef.current !== requestId) return;
      setSuggestions([]);
    } finally {
      if (requestIdRef.current === requestId) setLoadingSuggestions(false);
    }
  }, [api, industries, locations]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!industries.length || !locations.length) {
      setSuggestions([]);
      return undefined;
    }
    debounceRef.current = setTimeout(() => {
      loadSuggestions();
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [industries, locations, loadSuggestions]);

  async function prioritiseName(name, source) {
    const trimmed = String(name || '').trim();
    if (!trimmed || prioritisedKeys.has(normalize(trimmed))) return;

    setResolving(true);
    try {
      const resolved = await resolveV2Company(api, trimmed);
      const entry = {
        name: resolved.apolloName || trimmed,
        orgId: resolved.orgId || null,
        source,
      };
      onChange([...prioritised, entry]);
    } catch {
      onChange([...prioritised, { name: trimmed, orgId: null, source }]);
    } finally {
      setResolving(false);
    }
  }

  function removeEntry(index) {
    onChange(prioritised.filter((_, i) => i !== index));
  }

  async function addTypedEntry() {
    const trimmed = draft.trim();
    if (!trimmed) return;
    setDraft('');
    await prioritiseName(trimmed, 'typed');
  }

  return (
    <Stack gap="md">
      <Group gap="sm" align="baseline" justify="space-between" wrap="nowrap">
        <Group gap="sm" align="baseline" style={{ flex: 1, minWidth: 0 }}>
          <Text
            fw={400}
            style={{ color: 'var(--aviary-muted)', fontSize: 14, fontFeatureSettings: '"tnum"' }}
          >
            05
          </Text>
          <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 18, lineHeight: 1.3 }}>
            Prioritise these companies
          </Text>
        </Group>
        <Badge
          variant="light"
          color="gray"
          size="sm"
          radius="sm"
          style={{ flexShrink: 0, fontFamily: 'monospace', fontWeight: 500 }}
        >
          Apollo: organization_ids
        </Badge>
      </Group>

      <Text size="sm" style={{ color: 'var(--aviary-muted)', lineHeight: 1.5 }}>
        Top employers in your selected location. Anyone at a prioritised company scores higher fit and
        rises to the top of the contact list.
      </Text>

      {prioritised.length > 0 && (
        <Stack gap="xs">
          <Text size="xs" tt="uppercase" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
            Prioritised
          </Text>
          <Group gap="xs">
            {prioritised.map((entry, index) => {
              const unverified = !entry.orgId;
              const chip = (
                <SelectedChip
                  key={`${normalize(entry.name)}-${index}`}
                  label={entry.name}
                  prefix="★ "
                  muted={unverified}
                  onRemove={() => removeEntry(index)}
                  removeAriaLabel={`Remove ${entry.name}`}
                />
              );
              if (!unverified) return chip;
              return (
                <Tooltip
                  key={`${normalize(entry.name)}-${index}`}
                  label="Couldn't verify this company in Apollo — will still be shown to LLM for context but won't filter prospect search"
                  multiline
                  w={280}
                >
                  {chip}
                </Tooltip>
              );
            })}
          </Group>
        </Stack>
      )}

      <Stack gap="xs">
        <Group gap="xs" align="center">
          <Text size="xs" tt="uppercase" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.4 }}>
            Suggested from your industry + location — tap to prioritise
          </Text>
          {loadingSuggestions && <Loader size="xs" color="sage" />}
        </Group>
        <div style={{ display: 'flex', flexWrap: 'wrap', rowGap: 8, columnGap: 20 }}>
          {visibleSuggestions.map((name) => (
            <SuggestionChip
              key={name}
              label={name}
              disabled={resolving}
              onClick={() => prioritiseName(name, 'llm')}
            />
          ))}
        </div>
      </Stack>

      <Group gap="xs" align="flex-end" wrap="nowrap">
        <TextInput
          style={{ flex: 1 }}
          placeholder="+ Or type any company to prioritise…"
          value={draft}
          disabled={resolving}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTypedEntry();
            }
          }}
          size="md"
        />
        <ActionIcon
          size={42}
          color="sage"
          variant="filled"
          loading={resolving}
          onClick={addTypedEntry}
          aria-label="Add company"
        >
          <IconPlus size={20} />
        </ActionIcon>
      </Group>
    </Stack>
  );
}

export { industryItems, locationItems };
